import { useEffect, useRef, useState } from 'react';
import type { Map as MapboxMap, GeoJSONSource } from 'mapbox-gl';
import type { Pin, PinGeoJsonProperties } from '../types';

const PIN_SVG_TEMPLATE = (color: string, stroke: string) =>
  `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="64" viewBox="0 0 48 64"><path d="M24 62C24 62 4 36 4 22C4 11 13 2 24 2C35 2 44 11 44 22C44 36 24 62 24 62Z" fill="${color}" stroke="${stroke}" stroke-width="2"/><circle cx="24" cy="22" r="8" fill="white"/></svg>`;

const PIN_COLORS = [
  { id: 'pin-red', color: '#d32f2f', stroke: '#7b1a1a' },
  { id: 'pin-blue', color: '#1565c0', stroke: '#0d3c75' },
  { id: 'pin-purple', color: '#6a1b9a', stroke: '#38006b' },
  { id: 'pin-gold', color: '#f9a825', stroke: '#c17900' },
];

const SOURCE_ID = 'pins-source';
const CLUSTER_LAYER = 'clusters';
const CLUSTER_COUNT_LAYER = 'cluster-count';
const UNCLUSTERED_LAYER = 'unclustered-pins';

function pinsToGeoJson(pins: Pin[]): GeoJSON.FeatureCollection<GeoJSON.Point, PinGeoJsonProperties> {
  if (!pins || !Array.isArray(pins)) {
    return { type: 'FeatureCollection', features: [] };
  }

  return {
    type: 'FeatureCollection',
    features: pins.map((pin) => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [pin.longitude, pin.latitude],
      },
      properties: {
        id: pin.id,
        tripId: pin.tripId,
        title: pin.title,
        visitDate: pin.visitDate,
        thumbnailUrl: pin.thumbnailUrl,
        tripColor: pin.tripColor,
        photoCount: pin.photoCount || 0,
      },
    })),
  };
}

function loadPinImage(map: MapboxMap, id: string, svgString: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const img = new Image(48, 64);
    img.onload = () => {
      if (!map.hasImage(id)) {
        map.addImage(id, img);
      }
      resolve();
    };
    img.onerror = reject;
    img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgString);
  });
}

interface UseMapPinsOptions {
  map: MapboxMap | null;
  pins: Pin[];
  isLoaded: boolean;
  onPinClick?: (pin: Pin) => void;
}

/**
 * Hook that renders pins as GeoJSON with clustering on the map.
 * Uses custom SVG teardrop markers colored by photo count.
 */
export function useMapPins({ map, pins, isLoaded, onPinClick }: UseMapPinsOptions): void {
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const onPinClickRef = useRef(onPinClick);
  onPinClickRef.current = onPinClick;
  const pinsRef = useRef(pins);
  pinsRef.current = pins;

  // Load pin images
  useEffect(() => {
    if (!map || !isLoaded || imagesLoaded) return;

    const loadImages = async () => {
      try {
        await Promise.all(
          PIN_COLORS.map(({ id, color, stroke }) =>
            loadPinImage(map, id, PIN_SVG_TEMPLATE(color, stroke))
          )
        );
        setImagesLoaded(true);
      } catch (err) {
        console.error('[useMapPins] Failed to load pin images:', err);
      }
    };

    loadImages();
  }, [map, isLoaded]);

  // Add/update source and layers
  useEffect(() => {
    if (!map || !isLoaded || !imagesLoaded) return;
    if (!pins || !Array.isArray(pins)) return;

    const geojson = pinsToGeoJson(pins);

    // If source already exists, update data
    const existingSource = map.getSource(SOURCE_ID) as GeoJSONSource | undefined;
    if (existingSource) {
      existingSource.setData(geojson);
      return;
    }

    // Add source with clustering
    map.addSource(SOURCE_ID, {
      type: 'geojson',
      data: geojson,
      cluster: true,
      clusterMaxZoom: 14,
      clusterRadius: 50,
    });

    // Cluster circles layer
    map.addLayer({
      id: CLUSTER_LAYER,
      type: 'circle',
      source: SOURCE_ID,
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': [
          'step',
          ['get', 'point_count'],
          '#4caf50',
          10, '#ff9800',
          30, '#f44336',
        ],
        'circle-radius': [
          'step',
          ['get', 'point_count'],
          20, 10, 25, 30, 30,
        ],
        'circle-stroke-width': 2,
        'circle-stroke-color': '#fff',
      },
    });

    // Cluster count labels
    map.addLayer({
      id: CLUSTER_COUNT_LAYER,
      type: 'symbol',
      source: SOURCE_ID,
      filter: ['has', 'point_count'],
      layout: {
        'text-field': ['get', 'point_count_abbreviated'],
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 12,
      },
      paint: {
        'text-color': '#ffffff',
      },
    });

    // Unclustered pin markers
    map.addLayer({
      id: UNCLUSTERED_LAYER,
      type: 'symbol',
      source: SOURCE_ID,
      filter: ['!', ['has', 'point_count']],
      layout: {
        'icon-image': [
          'step',
          ['get', 'photoCount'],
          'pin-red',
          11, 'pin-blue',
          51, 'pin-purple',
          101, 'pin-gold',
        ],
        'icon-anchor': 'bottom',
        'icon-size': 0.6,
        'icon-allow-overlap': true,
      },
    });

    // Click handler for clusters - zoom in
    map.on('click', CLUSTER_LAYER, (e) => {
      const features = map.queryRenderedFeatures(e.point, { layers: [CLUSTER_LAYER] });
      if (!features.length) return;
      const clusterId = features[0].properties?.cluster_id;
      const source = map.getSource(SOURCE_ID) as GeoJSONSource;
      source.getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err) return;
        const geometry = features[0].geometry as GeoJSON.Point;
        map.easeTo({
          center: geometry.coordinates as [number, number],
          zoom: zoom || 14,
        });
      });
    });

    // Click handler for unclustered pins
    map.on('click', UNCLUSTERED_LAYER, (e) => {
      const features = map.queryRenderedFeatures(e.point, { layers: [UNCLUSTERED_LAYER] });
      if (!features.length) return;
      const props = features[0].properties as any;
      if (onPinClickRef.current && props.id) {
        const pin = pinsRef.current.find(p => p.id === props.id);
        if (pin) onPinClickRef.current(pin);
      }
    });

    // Cursor changes
    map.on('mouseenter', CLUSTER_LAYER, () => { map.getCanvas().style.cursor = 'pointer'; });
    map.on('mouseleave', CLUSTER_LAYER, () => { map.getCanvas().style.cursor = ''; });
    map.on('mouseenter', UNCLUSTERED_LAYER, () => { map.getCanvas().style.cursor = 'pointer'; });
    map.on('mouseleave', UNCLUSTERED_LAYER, () => { map.getCanvas().style.cursor = ''; });
  }, [map, isLoaded, pins, imagesLoaded]);

  // Cleanup layers on unmount
  useEffect(() => {
    return () => {
      if (!map) return;
      try {
      [UNCLUSTERED_LAYER, CLUSTER_COUNT_LAYER, CLUSTER_LAYER].forEach(layer => {
        if (map.getLayer(layer)) map.removeLayer(layer);
      });
      if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
      } catch { /* Map already destroyed */ }
    };
  }, [map]);
}
